// ---------------------
// TYPES
// ---------------------
export interface ModuleStatusConfig {
  labels: Record<number, string>;
  styles: Record<number, string>;
}

export type TextStatusConfig = Record<string, string>;
export type TextStatusAliasConfig = Record<string, string>;

export interface StatusConfig {
  textStatuses: TextStatusConfig;
  textStatusAliases: TextStatusAliasConfig;
  [moduleName: string]:
    | ModuleStatusConfig
    | TextStatusConfig
    | TextStatusAliasConfig;
}

// ---------------------
// CONFIG
// ---------------------
export const STATUS_CONFIG: StatusConfig = {
  // ===============================
  // 🔹 GLOBAL TEXT STATUSES
  // ===============================
  textStatuses: {
    Active: "bg-[#00b65226] text-[#0a8442]",
    Inactive: "bg-[#f3f4f6] text-[#374151]",
    Deleted: "bg-[#F4D9D9] text-[#B0191C]",
    Completed: "bg-[#bbf7d0] text-[#166534]",
    Booked: "bg-[#00b65226] text-[#0a8442]",
    Failed: "bg-[#fee2e2] text-[#b91c1c]",
    Pending: "bg-[#E9890326] text-[#B08019]",
    Rejected: "bg-[#fee2e2] text-[#b91c1c]",
    CASA: "bg-[#F5F5F5]  text-[#4F4F4F]",
    DEPOSIT: "bg-[#F5F5F5]  text-[#4F4F4F]",
    LOAN: "bg-[#F5F5F5]  text-[#4F4F4F]",
    CARD: "bg-[#F5F5F5]  text-[#4F4F4F]",
    Suspended: "bg-[#E9890326] text-[#B08019]",
    Scheduled: "bg-[#fef3c7] text-[#b45309]",
    COMPLETED: "bg-gray-100 text-gray-600",
    ACTIVE: "bg-[#dcfce7] text-[#15803d]",
    CANCELED: "bg-[#fee2e2] text-[#b91c1c] ",
    ON_HOLD: "bg-[#fef9c3] text-[#ca8a04]",
    "Past Due": "bg-[#fee2e2] text-[#b91c1c]",
    PENDING_RESPONSE: "bg-[#fef9c3] text-[#ca8a04]",
  },

  // ===============================
  // 🔹 TEXT STATUS ALIASES
  // ===============================
  textStatusAliases: {
    A: "Active",
    B: "Booked",
    C: "Completed",
    D: "Deleted",
    I: "Inactive",
    F: "Failed",
    P: "Pending",
    R: "Rejected",
    S: "Suspended",
    COMPLETED: "Completed",
    ACTIVE: "Active",
    CANCELED: "Canceled",
    ON_HOLD: "On Hold",
    "Past Due": "Past Due",
    PENDING_RESPONSE: "Pending Response",
  },

  // ===============================
  // 🔶 PAYMENTS MODULE
  // ===============================
  payments: {
    labels: {
      4410: "Save as Draft",
      4415: "Awaiting Approval",
      4420: "Sent for Repair",
      4425: "Rejected by Approver",
      4445: "Approved by Approver",
      4446: "Self Approved",
      4480: "Future-dated Payment",
      4481: "Scheduled",
      4450: "Pending With Bank",
      4455: "Pending With Bank",
      4465: "In Process with Bank",
      4430: "Rejected by Bank",
      4440: "Processed by Bank",
      4485: "Future Dated - Payment Started",
      4490: "Future Dated - Payment Completed",
      4447: "Pending for consent authorization",
      910: "Failed", //"Technical Error"
      911: "Failed", //"Remote Service Error"
      900: "Failed", //"Timeout Error"
    },
    styles: {
      4410: "bg-[#f3f4f6] text-[#374151]",
      4415: "bg-[#fef9c3] text-[#ca8a04]",
      4420: "bg-[#e0f2fe] text-[#0369a1]",
      4425: "bg-[#fee2e2] text-[#b91c1c]",
      4445: "bg-[#dcfce7] text-[#15803d]",
      4446: "bg-[#d1fae5] text-[#047857]",
      4480: "bg-[#fef3c7] text-[#b45309]",
      4481: "bg-[#fef9c3] text-[#ca8a04]",
      4450: "bg-[#fef9c3] text-[#ca8a04]",
      4455: "bg-[#fef9c3] text-[#ca8a04]",
      4447: "bg-[#fef9c3] text-[#ca8a04]",
      4465: "bg-[#dbeafe] text-[#1d4ed8]",
      4430: "bg-[#fee2e2] text-[#b91c1c]",
      4440: "bg-[#dcfce7] text-[#15803d]",
      4485: "bg-[#fef3c7] text-[#92400e]",
      4490: "bg-[#d9f99d] text-[#4d7c0f]",
      910: "bg-red-100 text-red-500",
      900: "bg-red-100 text-red-500",
      911: "bg-red-100 text-red-500",
    },
  },

  // ===============================
  // 🔶 RTP MODULE
  // ===============================
  rtp: {
    labels: {
      5000: "Approved",
      5001: "Self Approved",
      5010: "Save as Draft",
      5015: "Awaiting Approval",
      5020: "Rejected by Approver",
      5030: "Deleted",
    },
    styles: {
      5000: "bg-[#dcfce7] text-[#15803d]",
      5001: "bg-[#d1fae5] text-[#047857]",
      5010: "bg-[#f3f4f6] text-[#374151]",
      5015: "bg-[#e1eaff] text-[#007bff]",
      5020: "bg-[#fee2e2] text-[#b91c1c]",
      5030: "bg-[#f3f4f6] text-[#374151]",
    },
  },

  // ===============================
  // 🔶 BENEFICIARY MODULE
  // ===============================
  beneficiary: {
    labels: {
      3015: "Awaiting Approval",
      3000: "Approved",
      3020: "Rejected by Approver",
      3001: "Self Approved",
      3025: "User Removed This Beneficiary",
    },
    styles: {
      3015: "bg-[#e1eaff] text-[#007bff]",
      3000: "bg-[#dcfce7] text-[#15803d]",
      3020: "bg-[#fee2e2] text-[#b91c1c]",
      3001: "bg-[#d1fae5] text-[#047857]",
      3025: "bg-[#f3f4f6] text-[#374151]",
    },
  },

  // ===============================
  // 🔶 UploadFile MODULE
  // ===============================
  bulk: {
    labels: {
      1234: "Validation in Progress",
      2345: "Validation Failed",
      7505: "Validation Failed",
      3456: "Ready for Review",
      4567: "Pending Approval",
      5678: "Rejected",
      4506: "Pending",
      7506: "Pending",
      6789: "Completed",
      7890: "Pending with Bank",
      4500: "Upload Success",
      4511: "Uploaded",
      4620: "File Uploaded",
      4551: "Received",
      4670: "File Received",
      7670: "File Received",
      4513: "Submited",
      4640: "File Submitted",
      7640: "File Submitted",
      7500: "Upload Success",
      4445: "Approved By Approver",
      4502: "Validation In Progress",
      4505: "Validation Failed",
      4510: "Ready For Review",
      7510: "Ready For Review",
      4515: "Awaiting Approval",
      7515: "Awaiting Approval",
      4415: "Awaiting Approval",
      7415: "Awaiting Approval",
      4550: "In Process",
      4580: "Future Dated",
      4585: "Rejected",
      4425: "Rejected By Approver",
      4590: "Processed",
      7580: "Scheduled",
      7585: "Rejected",
      7550: "In Process",
      4507: "Valid",
      7507: "Valid",
      4508: "Invalid",
      7508: "Invalid",
      4450: "Pending With Bank",
      7450: "Pending With Bank",
      4547: "Pending For Consent Authorization",
      7547: "Pending For Consent Authorization",
      4600: "Pending File Upload to Bank",
      7600: "Pending File Upload to Bank",
      4480: "Future Dated Payment",
      4440: "Processed by Bank",
      7440: "Processed by Bank",
      4430: "Rejected by Bank",
      7430: "Rejected by Bank",
      4685: "File Rejected",
    },
    styles: {
      1234: "bg-[#78787826] text-[#787878]",
      7550: "bg-[#78787826] text-[#787878]",
      2345: "bg-[#B6000326] text-[#B0191C]",
      7510: "bg-[#fef9c3] text-[#ca8a04]",
      7580: "bg-[#fef9c3] text-[#ca8a04]",
      4567: "bg-[#E9890326] text-[#B08019]",
      7585: "bg-[#B6000326] text-[#B0191C]",
      6789: "bg-[#00B65226] text-[#0A8442]",
      7890: "bg-[#E9890326] text-[#B08019]",
      4506: "bg-[#E9890326] text-[#B08019]",
      7506: "bg-[#E9890326] text-[#B08019]",

      // ========================
      4500: "bg-[#00B65226] text-[#0A8442]",
      4511: "bg-[#00B65226] text-[#0A8442]",
      4620: "bg-[#00B65226] text-[#0A8442]",
      4551: "bg-[#00B65226] text-[#0A8442]",
      4670: "bg-[#00B65226] text-[#0A8442]",
      7670: "bg-[#00B65226] text-[#0A8442]",
      4513: "bg-[#00B65226] text-[#0A8442]",
      4640: "bg-[#00B65226] text-[#0A8442]",
      7500: "bg-[#00B65226] text-[#0A8442]",
      4445: "bg-[#00B65226] text-[#0A8442]",
      4507: "bg-[#00B65226] text-[#0A8442]",
      7507: "bg-[#00B65226] text-[#0A8442]",
      4502: "bg-[#78787826] text-[#787878]",
      4505: "bg-[#B6000326] text-[#B0191C]",
      7505: "bg-[#B6000326] text-[#B0191C]",
      4510: "bg-[#fef9c3] text-[#ca8a04]",
      4515: "bg-[#e1eaff] text-[#007bff]",
      7515: "bg-[#e1eaff] text-[#007bff]",
      4415: "bg-[#e1eaff] text-[#007bff]",
      7415: "bg-[#e1eaff] text-[#007bff]",
      4550: "bg-[#78787826] text-[#787878]",
      4580: "bg-[#E9890326] text-[#B08019]",
      4450: "bg-[#E9890326] text-[#B08019]",
      7450: "bg-[#E9890326] text-[#B08019]",
      4547: "bg-[#E9890326] text-[#B08019]",
      7547: "bg-[#E9890326] text-[#B08019]",
      4600: "bg-[#E9890326] text-[#B08019]",
      7600: "bg-[#E9890326] text-[#B08019]",
      4480: "bg-[#E9890326] text-[#B08019]",
      4585: "bg-[#B6000326] text-[#B0191C]",
      4425: "bg-[#B6000326] text-[#B0191C]",
      4508: "bg-[#B6000326] text-[#B0191C]",
      7508: "bg-[#B6000326] text-[#B0191C]",
      4590: "bg-[#00B65226] text-[#0A8442]",
      4440: "bg-[#dcfce7] text-[#15803d]",
      7440: "bg-[#dcfce7] text-[#15803d]",
      4430: "bg-[#fee2e2] text-[#b91c1c]",
      7430: "bg-[#fee2e2] text-[#b91c1c]",
      4685: "bg-[#fee2e2] text-[#b91c1c]",
    },
  },
};
